import ee from '../../services/earth-engine';

/**
 * @function downloadEEImage
 * @description Downloads one Earth Engine image as a cloud-optimised GeoTIFF.
 * `getDownloadURL` answers through a callback, and the scale has to be a plain number: called
 * synchronously, or handed an `ee.Number`, it returns a one-pixel file rather than an error.
 * @param {Object} image - The image to write, as an `ee.Image`.
 * @param {Object} region - The extent to write, in EPSG:4326.
 * @param {Number} scale - Pixel size in metres, as a plain number.
 * @param {String} name - File name, without the extension.
 * @memberof module:Bathymetry
 * @returns {Promise<void>} Resolves once the download has been triggered.
 */
export const downloadEEImage = (image, region, scale, name) =>
  new Promise((resolve, reject) => {
    const params = {
      region,
      scale: Number(scale),
      format: 'GEO_TIFF',
      maxPixels: 1e10,
      bestEffort: true,
      formatOptions: { cloudOptimized: true },
    };
    image.getDownloadURL(params, async (url, err) => {
      if (err) return reject(err);
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const link = document.createElement('a');
        const blobUrl = window.URL.createObjectURL(blob);
        link.href = blobUrl;
        link.download = `${name}.tif`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        resolve();
      } catch (e) {
        reject(e);
      }
    });
  });

/**
 * @function downloadCSV
 * @description Triggers a browser download of a CSV string.
 * @param {String} csvContent - The file's contents.
 * @param {String} name - File name, without the extension.
 * @memberof module:Bathymetry
 * @returns {void}
 */
export const downloadCSV = (csvContent, name) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = `${name}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * @function convertFeaturesToCSV
 * @description Turns evaluated features into a CSV, one row each, missing properties left empty.
 * @param {Array<Object>} features - Features already evaluated client-side.
 * @param {Array<String>} selectors - The columns, in order.
 * @memberof module:Bathymetry
 * @returns {String} The CSV, header first.
 */
export const convertFeaturesToCSV = (features, selectors) => {
  const header = selectors.join(',');
  const rows = features.map((feature) =>
    selectors.map((selector) => (feature.properties || {})[selector] ?? '').join(','),
  );
  return [header, ...rows].join('\n');
};

/**
 * @function evaluateEEFeatures
 * @description Evaluates a collection and returns its features as a plain array. Converted to a
 * list first, so the answer comes back as an array rather than as GeoJSON.
 * @param {Object} fcSelector - The collection to read.
 * @memberof module:Bathymetry
 * @returns {Promise<Array<Object>>} The evaluated features.
 */
export const evaluateEEFeatures = (fcSelector) =>
  new Promise((resolve, reject) => {
    const collection = ee.FeatureCollection(fcSelector);
    collection.toList(collection.size()).evaluate((result, error) => (error ? reject(error) : resolve(result)));
  });
